// ---------------------------------------------------------------------------
// rapid-trigger.js -- mounts the Rapid Trigger demo into a theme section or
// block. Loaded as a module so the browser evaluates it exactly once however
// many instances a page carries.
//
// Everything is scoped to a root element ([data-rt-root]); there are no
// document-level ids, so several instances can share a page. Per-instance
// configuration comes from a JSON <script data-rt-config> inside the root,
// written by the Liquid snippet from the section / block settings.
//
// Depends on window.RT (sim), window.RTI18N (strings), window.RTRender
// (renderer) and window.GIFENC, which the snippet loads ahead of this file.
// ---------------------------------------------------------------------------

const DEFAULTS = {
  motion: 'multi-actuation',
  showExport: true,
  language: 'auto',
  regular: { travel: 3.4, actuation: 1.2 },
  analog:  { travel: 3.4, actuation: 0.7, release: 3.0, sense: 0.06 },
};

const FPS = 60;
const FRAME_MS = 1000 / FPS;
const EXPORT_FPS = 15;

// Safety net for very large displays, not a quality setting. Every pixel here
// is rasterised and handed to the compositor on each drawn frame, so the
// displayed size drives GPU load quadratically; the section's max-width
// setting is the intended lever.
const MAX_BACKING_PX = 4500000;

function readConfig(root) {
  const el = root.querySelector('script[data-rt-config]');
  let cfg = {};
  try { cfg = JSON.parse(el ? el.textContent : '{}') || {}; } catch (_) { cfg = {}; }
  const num = (v, d) => (Number.isFinite(+v) && v !== null && v !== '' ? +v : d);
  return {
    motion: cfg.motion || DEFAULTS.motion,
    showExport: cfg.showExport !== false,
    language: cfg.language || DEFAULTS.language,
    storeLocale: cfg.storeLocale || '',
    regular: {
      travel:    num(cfg.regular && cfg.regular.travel,    DEFAULTS.regular.travel),
      actuation: num(cfg.regular && cfg.regular.actuation, DEFAULTS.regular.actuation),
    },
    analog: {
      travel:    num(cfg.analog && cfg.analog.travel,    DEFAULTS.analog.travel),
      actuation: num(cfg.analog && cfg.analog.actuation, DEFAULTS.analog.actuation),
      release:   num(cfg.analog && cfg.analog.release,   DEFAULTS.analog.release),
      sense:     num(cfg.analog && cfg.analog.sense,     DEFAULTS.analog.sense),
    },
  };
}

function mount(root) {
  if (root.dataset.rtMounted) return root.__rt;
  root.dataset.rtMounted = '1';

  const { MOTIONS, World, MAX_TRAVEL, RELEASE_GAP, clamp } = window.RT;
  const { render, geometry, barAt, contentBounds, LAYOUTS, C } = window.RTRender;
  const I18N = window.RTI18N;
  const $ = (name) => root.querySelector(`[data-rt="${name}"]`);
  const cfg = readConfig(root);

  // Language: the section setting when it names one, otherwise whatever
  // locale Shopify rendered the page in (request.locale, written into the
  // config by the snippet), otherwise the document's own lang. A storefront
  // language switch is a navigation, so the page and this block re-render
  // together; nothing has to watch for a change.
  const T = I18N.pick(cfg.language !== 'auto' ? cfg.language
    : cfg.storeLocale || document.documentElement.lang);
  root.setAttribute('lang', T.locale);
  I18N.apply(root, T);

  const world = new World();
  Object.assign(world.regular.params, cfg.regular);
  Object.assign(world.analog.params, cfg.analog);
  if (MOTIONS[cfg.motion]) world.motionKey = cfg.motion;
  world.resetRun();

  const canvas = $('stage');
  const ctx = canvas.getContext('2d');
  const sceneEl = root.querySelector('.rt-scene');
  const stageEl = root.querySelector('.rt-stage');

  // Wide or compact composition, chosen by how much room the block actually
  // has. Below the breakpoint the HTML controls have already dropped out of
  // the overlay (see the CSS container query at the same width), and the
  // canvas switches to a tighter layout with proportionally larger text so it
  // renders near 1:1 on a phone instead of being scaled to a third of its size.
  const COMPACT_BELOW = 640;
  let layout = LAYOUTS.wide;
  function pickLayout() {
    const w = stageEl.getBoundingClientRect().width;
    const want = w > 0 && w < COMPACT_BELOW ? LAYOUTS.compact : LAYOUTS.wide;
    if (want === layout) return false;
    layout = want;
    sceneEl.style.setProperty('--rt-aspect', `${layout.w} / ${layout.h}`);
    lastSig = '';
    return true;
  }
  const statusEl = $('status');
  const pp = $('playpause');
  const exportBtn = $('export');

  // Nothing is scheduled until the visitor starts the simulation: no rAF
  // loop, no stepping, one static frame. A visitor who scrolls past this
  // block pays nothing for it.
  let started = false;
  let playing = false;
  let loopActive = false;
  let visible = true;
  let dead = false;
  let forceRedraw = true;

  // --- Backing store: exactly the displayed size in device pixels, so the
  // drawing is as crisp as the DOM controls over it.
  function sizeCanvas() {
    const switched = pickLayout();
    const r = canvas.getBoundingClientRect();
    if (!r.width) return;
    const dpr = window.devicePixelRatio || 1;
    const s = Math.max(1, Math.min(
      (r.width * dpr) / layout.w,
      Math.sqrt(MAX_BACKING_PX / (layout.w * layout.h))));
    const bw = Math.round(layout.w * s), bh = Math.round(layout.h * s);
    if (!switched && canvas.width === bw && canvas.height === bh) return;
    canvas.width = bw;
    canvas.height = bh;
    ctx.setTransform(s, 0, 0, s, 0, 0);
    forceRedraw = true;
    if (!started) drawOnce();
  }
  const onResize = () => sizeCanvas();
  window.addEventListener('resize', onResize);

  // --- Motion presets
  const presetsEl = $('presets');
  const buttons = {};
  for (const key of Object.keys(MOTIONS)) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'rt-seg';
    b.textContent = T.motions[key];
    b.addEventListener('click', () => selectMotion(key));
    presetsEl.appendChild(b);
    buttons[key] = b;
  }
  function selectMotion(key) {
    world.motionKey = key;
    world.resetRun();
    paramsVersion++;
    for (const [k, b] of Object.entries(buttons)) {
      b.setAttribute('aria-pressed', String(k === key));
    }
    if (exportBtn) exportBtn.disabled = !MOTIONS[key].period;
    if (!started) drawOnce();
  }

  // --- Parameters: one typed mm field each. Analog bounds chain travel ->
  // actuation -> release, each depending only on fields earlier in the list,
  // so a single ordered pass of syncControls settles any cascade (shrinking
  // travel pulls actuation down, which pulls release down with it).
  //. Bounds are live and mutually
  // constraining, and every field is re-clamped after any edit, so the config
  // can't be typed into an invalid state.
  const R = () => world.regular.params;
  const A = () => world.analog.params;

  const CONTROLS = [
    { id: 'speed', step: 0.05, dp: 2, bounds: () => [0.1, 2.0],
      get: () => world.speed, set: v => { world.speed = v; }, quiet: true },
    { id: 'r-travel', step: 0.1, dp: 2, bounds: () => [1.0, MAX_TRAVEL],
      get: () => R().travel, set: v => { R().travel = v; } },
    { id: 'r-act', step: 0.05, dp: 2, bounds: () => [0.1, R().travel],
      get: () => R().actuation, set: v => { R().actuation = v; } },
    { id: 'a-travel', step: 0.1, dp: 2, bounds: () => [1.0, MAX_TRAVEL],
      get: () => A().travel, set: v => { A().travel = v; } },
    { id: 'a-act', step: 0.05, dp: 2, bounds: () => [0.05, A().travel - 2 * RELEASE_GAP],
      get: () => A().actuation, set: v => { A().actuation = v; } },
    { id: 'a-release', step: 0.05, dp: 2,
      bounds: () => [A().actuation + RELEASE_GAP, A().travel - RELEASE_GAP],
      get: () => A().release, set: v => { A().release = v; } },
    { id: 'a-sense', step: 0.005, dp: 3, bounds: () => [0.005, 1.0],
      get: () => A().sense, set: v => { A().sense = v; } },
  ].filter(c => root.querySelector(`[data-rt-field="${c.id}"]`));

  for (const c of CONTROLS) {
    c.num = root.querySelector(`[data-rt-field="${c.id}"]`);
    // The HTML step is the field's precision (0.01 / 0.001), not the arrow
    // nudge: with step=0.05 the browser would flag a legitimate 1.23 as
    // invalid. Arrow keys are handled below and keep the coarser c.step.
    c.num.step = 10 ** -c.dp;
    c.num.addEventListener('input', () => {
      // Refuse a third (or fourth) decimal as it is typed rather than
      // silently rounding it away on blur, so the field never shows a
      // precision the value doesn't have.
      const m = c.num.value.match(/^(-?\d*)(?:\.(\d*))?$/);
      if (m && m[2] && m[2].length > c.dp) {
        c.num.value = m[1] + '.' + m[2].slice(0, c.dp);
      }
      const v = parseFloat(c.num.value);
      if (Number.isFinite(v)) commit(c, v);
    });
    c.num.addEventListener('change', () => {
      const v = parseFloat(c.num.value);
      commit(c, Number.isFinite(v) ? v : c.get());
      c.num.value = c.get().toFixed(c.dp);
    });
    c.num.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      commit(c, c.get() + (e.key === 'ArrowUp' ? c.step : -c.step));
      c.num.value = c.get().toFixed(c.dp);
    });
  }

  function commit(c, value) {
    const [lo, hi] = c.bounds();
    // Round after clamping: the bounds themselves can carry float noise
    // (actuation + 0.1 = 0.7999999999999999), and the stored value must be
    // exactly what the field displays.
    c.set(+clamp(value, lo, hi).toFixed(c.dp));
    syncControls();
    paramsVersion++;
    if (!c.quiet) world.resetRun();
    if (!started) drawOnce();
  }

  function syncControls() {
    for (const c of CONTROLS) {
      const [lo, hi] = c.bounds();
      const v = clamp(c.get(), lo, hi);
      c.set(v);
      // Rounded, or bounds like actuation + 0.1 arrive as 0.7999999999999999
      // and the browser marks a correct entry of 0.80 as out of range.
      c.num.min = +lo.toFixed(3); c.num.max = +hi.toFixed(3);
      if (document.activeElement !== c.num) c.num.value = v.toFixed(c.dp);
    }
  }

  $('restart').addEventListener('click', () => {
    world.resetRun();
    paramsVersion++;
    if (!started) drawOnce();
  });
  pp.addEventListener('click', () => {
    if (!started) return;
    playing = !playing;
    pp.textContent = playing ? T.ui.pause : T.ui.play;
  });

  // --- Manual drag, only when the pointer is actually on a bar.
  let dragging = false;
  const localPoint = (e) => {
    const r = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (layout.w / r.width),
      y: (e.clientY - r.top) * (layout.h / r.height),
    };
  };
  const depthAt = (y) => clamp(geometry(world, layout).mmOf(y), 0, world.maxTravel);

  canvas.addEventListener('pointerdown', (e) => {
    if (!started) return;
    const p = localPoint(e);
    if (barAt(world, p.x, p.y, layout) < 0) return;
    dragging = true;
    canvas.setPointerCapture(e.pointerId);
    if (world.motionKey !== 'manual') selectMotion('manual');
    world.manualDepth = depthAt(p.y);
  });
  canvas.addEventListener('pointermove', (e) => {
    const p = localPoint(e);
    if (dragging) { world.manualDepth = depthAt(p.y); return; }
    const want = started && barAt(world, p.x, p.y, layout) >= 0 ? 'ns-resize' : 'default';
    if (canvas.style.cursor !== want) canvas.style.cursor = want;
  });
  canvas.addEventListener('pointerup', () => { dragging = false; });
  canvas.addEventListener('pointercancel', () => { dragging = false; });

  // --- GIF export: one loop, rendered off-screen from a fresh World seeded
  // with the live parameters, cropped to the artwork. Deterministic and
  // independent of frame rate, speed, or what the live view is doing.
  if (exportBtn) exportBtn.addEventListener('click', async () => {
    const motion = MOTIONS[world.motionKey];
    if (!motion.period) return;
    exportBtn.disabled = true;
    const label = exportBtn.textContent;
    try {
      const n = Math.round(motion.period * EXPORT_FPS);
      const dt = motion.period / n;
      const off = document.createElement('canvas');
      const octx = off.getContext('2d', { willReadFrequently: true });

      const w2 = new World();
      Object.assign(w2.regular.params, world.regular.params);
      Object.assign(w2.analog.params, world.analog.params);
      w2.motionKey = world.motionKey;
      w2.speed = 1;
      w2.resetRun();

      const MARGIN = 32;
      const b = contentBounds(octx, w2, T);
      const cw = Math.round(b.w + MARGIN * 2);
      const ch = Math.round(b.h + MARGIN * 2);
      const ox = b.x - MARGIN, oy = b.y - MARGIN;
      off.width = cw;
      off.height = ch;

      const frames = [], delays = [];
      const cs = t => Math.round(t * motion.period * 100 / n);
      for (let i = 0; i < n; i++) {
        octx.setTransform(1, 0, 0, 1, 0, 0);
        octx.fillStyle = C.bg;
        octx.fillRect(0, 0, cw, ch);
        octx.setTransform(1, 0, 0, 1, -ox, -oy);
        render(octx, w2, LAYOUTS.wide, T);
        frames.push(octx.getImageData(0, 0, cw, ch).data);
        delays.push(cs(i + 1) - cs(i));
        w2.advance(dt);
        exportBtn.textContent = `${i + 1}/${n}`;
        await new Promise(r => setTimeout(r, 0));
      }
      exportBtn.textContent = T.ui.encoding;
      await new Promise(r => setTimeout(r, 0));
      const bytes = window.GIFENC.encode(frames, cw, ch, delays);

      const blob = new Blob([bytes], { type: 'image/gif' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `rapid-trigger-${world.motionKey}.gif`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
      if (statusEl) statusEl.textContent = `${cw}x${ch} · ${(bytes.length / 1024).toFixed(0)} KB`;
    } catch (err) {
      if (statusEl) statusEl.textContent = T.ui.exportFailed;
    } finally {
      exportBtn.textContent = label;
      exportBtn.disabled = !MOTIONS[world.motionKey].period;
    }
  });

  // --- Redraw gating: if none of the numbers the scene depends on moved,
  // there is nothing to draw.
  let paramsVersion = 0;
  let lastSig = '';
  function sceneSignature() {
    const r = world.regular, a = world.analog, z = a.senseZone();
    const q = v => Math.round(v * 2000);
    const f = sw => Math.min(sw.msSincePress, 400);
    return `${paramsVersion}|${q(r.depth)},${r.on ? 1 : 0},${r.presses},${f(r)}` +
           `|${q(a.depth)},${a.on ? 1 : 0},${a.presses},${f(a)},${q(z.from)},${q(z.to)}`;
  }

  // --- Loop, paced to 60 fps by a running deadline (a minimum gap can only
  // skip whole refreshes, which lands on 55 fps on a 165 Hz display).
  let last = performance.now();
  let nextDraw = 0;
  function frame(now) {
    // Stop rescheduling when the block is off-screen or torn down. The
    // IntersectionObserver restarts the loop when it scrolls back into view.
    if (dead || !visible) { loopActive = false; return; }
    requestAnimationFrame(frame);
    if (now < nextDraw) return;
    nextDraw += FRAME_MS;
    if (nextDraw < now) nextDraw = now + FRAME_MS;

    const dt = (now - last) / 1000;
    last = now;
    if (playing) world.advance(dt);

    const sig = sceneSignature();
    if (forceRedraw || sig !== lastSig) {
      render(ctx, world, layout, T);
      lastSig = sig;
      forceRedraw = false;
    }
  }
  function startLoop() {
    if (loopActive || dead || !started || !visible) return;
    loopActive = true;
    last = performance.now();
    nextDraw = 0;
    requestAnimationFrame(frame);
  }

  function drawOnce() {
    render(ctx, world, layout, T);
    lastSig = sceneSignature();
    forceRedraw = false;
  }

  // Pause the whole thing while scrolled out of view. On a long product page
  // this is the difference between a block that costs nothing once you have
  // passed it and one that keeps the GPU busy behind the fold.
  let io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver((entries) => {
      visible = entries.some(e => e.isIntersecting);
      if (visible) startLoop();
    }, { threshold: 0.05 });
    io.observe(canvas);
  }

  $('start-btn').addEventListener('click', () => {
    if (started) return;
    started = true;
    playing = true;
    pp.disabled = false;
    pp.textContent = T.ui.pause;
    $('start-overlay').hidden = true;
    startLoop();
  });

  syncControls();
  selectMotion(world.motionKey);
  sizeCanvas();
  drawOnce();

  const api = {
    destroy() {
      dead = true;
      window.removeEventListener('resize', onResize);
      if (io) io.disconnect();
      delete root.dataset.rtMounted;
      delete root.__rt;
    },
  };
  root.__rt = api;
  return api;
}

// --- Auto-mount --------------------------------------------------------------
function mountAll(scope) {
  (scope || document).querySelectorAll('[data-rt-root]').forEach(mount);
}
function unmountAll(scope) {
  (scope || document).querySelectorAll('[data-rt-root]').forEach(el => {
    if (el.__rt) el.__rt.destroy();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountAll());
} else {
  mountAll();
}

// Theme editor: sections are re-rendered when the merchant edits settings, so
// mount into freshly loaded sections and tear down the ones being replaced.
document.addEventListener('shopify:section:load',   (e) => mountAll(e.target));
document.addEventListener('shopify:section:unload', (e) => unmountAll(e.target));
document.addEventListener('shopify:block:select',   (e) => mountAll(e.target));

window.RapidTriggerDemo = { mount, mountAll, unmountAll };
